import type { ZoneBuilder } from '../types';
import { scatterPeople } from '../crowd';
import {
  BOAT_COLORS,
  FOLIAGE,
  MED_SEA,
  NAPOLI_PASTEL,
  NAPOLI_TUFA,
  SCOOTER_COLORS,
  darken,
} from '../palettes';

/**
 * scenery/zones/napoli.ts — vivid Mediterranean noon. Tight pastel alleys with
 * laundry strung overhead → the open bay (Vesuvius smoking across the water)
 * → the heavy tufa castle quarter → a grand cream piazza for the finale.
 * The bay lies on side +1 (the right, like London's Thames).
 */

export const BAY_SIDE = 1;

/** The lungomare's course span (cities.ts route: 0.30 → 0.62) and how far the
 *  bay takes to open at each end. Mirrored — deliberately, per the batch rule
 *  against cross-file coupling — by shortcutNapoli's BAY_FROM/BAY_TO guard. */
const BAY_FROM = 0.3;
const BAY_TO = 0.62;
const BAY_FEATHER = 0.016; // ≈ 35 u of course

/** Deterministic 0..1 from an integer — per-instance variety that costs NO rng
 *  draw, so every other instance in every other city stays byte-identical
 *  (mirrors shortcutMeshes' `art`). */
function hash01(i: number): number {
  const s = Math.sin(i * 127.1 + 311.7) * 43758.5453;
  return s - Math.floor(s);
}

const spaccanapoli: ZoneBuilder = ({ rng, peopleRng, hw, at, s, a, side }) => {
  // Tall, narrow, shoulder-to-shoulder pastel houses pressed close to the road.
  if (rng.next() < 0.92) {
    const w = rng.range(6, 10);
    const h = rng.range(8, 16);
    const d = rng.range(8, 12);
    const lat = hw + 2 + w / 2 + rng.range(0, 3);
    const [x, y, z] = at(lat);
    const color = rng.pick(NAPOLI_PASTEL);
    a.buildings.push({ x, y, z, rotY: s.dir, w, h, d, color });
    a.plain.push({ x, y: y + h - 0.4, z, rotY: s.dir, w: w + 0.7, h: 0.9, d: d + 0.7, color: darken(color, 0.66) });
    // The Napoli signatures (M14): wrought-iron BALCONIES jutting over the
    // alley with GREEN SHUTTER pairs beside the windows — every floor of the
    // old town wears them.
    const floors = Math.min(3, Math.floor(h / 4));
    for (let f = 0; f < floors; f++) {
      const by = y + 3.4 + f * 3.6;
      if (by > y + h - 1.5) break;
      const [bx, , bz] = at(lat - w / 2 - 0.55);
      if (rng.next() < 0.75) {
        // THE BALCONY. The old pair — a near-black 0.14 slab under a SOLID
        // 0.85 × 0.85 "iron rail mass" — read at street level as a heavy dark
        // billboard bolted to the facade (QA: deck-street-level,
        // block-houses-check; the shutters got their real-leaves pass in the
        // same round and these were left behind). A real Neapolitan balcony is
        // a pale PIPERNO stone tray on corbels carrying an AIRY iron
        // balustrade — you see the wall through it, and the tray is the
        // lightest thing on the facade, not the darkest.
        //
        // Every extra piece is a plain push (a push costs no rng draw), and the
        // ONE `rng.next()` gate above is unchanged — so no draw order moves.
        const fx2 = Math.sin(s.dir);
        const fz2 = Math.cos(s.dir);
        // The balustrade line stands at the tray's OUTER edge (toward the
        // alley), not through its middle — a rail down the centre of the tray
        // is what made the old mass read as a wall.
        const [ox, , oz] = at(lat - w / 2 - 1.06);
        a.plain.push({ x: bx, y: by, z: bz, rotY: s.dir, w: 1.25, h: 0.16, d: 2.7, color: 0xc9bfa9 }); // stone tray
        a.plain.push({ x: bx, y: by - 0.17, z: bz, rotY: s.dir, w: 0.95, h: 0.18, d: 2.2, color: 0xa2967d }); // corbel course
        a.plain.push({ x: ox, y: by + 0.86, z: oz, rotY: s.dir, w: 0.1, h: 0.08, d: 2.72, color: 0x454b55 }); // hand rail
        a.plain.push({ x: ox, y: by + 0.34, z: oz, rotY: s.dir, w: 0.08, h: 0.05, d: 2.66, color: 0x3d434c }); // knee rail
        for (const t of [-1.3, -0.44, 0.44, 1.3]) {
          a.plain.push({
            x: ox + fx2 * t,
            y: by + 0.47,
            z: oz + fz2 * t,
            rotY: s.dir,
            w: 0.09,
            h: 0.78,
            d: 0.09,
            color: 0x3d434c,
          }); // balusters (the corner pair doubles as the end posts)
        }
      }
      // PERSIANE. The old single 0.18 × 2.9 slab standing 0.12 proud of the
      // wall read as one big green panel stuck to the facade (QA: alley-chase).
      // Real Neapolitan shutters are two narrow leaves sitting flush in their
      // reveal — and on any given alley a fair share stand ajar. Variety comes
      // from an index hash, never from `rng`, so no draw is disturbed.
      const fx = Math.sin(s.dir);
      const fz = Math.cos(s.dir);
      const bId = Math.round(s.distance * 3) + f * 5;
      for (const lf of [-1, 1] as const) {
        const ajar = hash01(bId + (lf + 1)) < 0.35;
        const [lx, , lz] = at(lat - w / 2 - (ajar ? 0.3 : 0.05));
        const along = lf * (ajar ? 0.95 : 0.78);
        a.plain.push({
          x: lx + fx * along,
          y: by + 0.9,
          z: lz + fz * along,
          rotY: s.dir + (ajar ? lf * 0.55 : 0),
          w: 0.09,
          h: 1.6,
          d: 0.62,
          color: 0x2e6a48,
        });
      } // shutter pair
    }
  }
  // Laundry lines strung ACROSS the street (once per step, not per side).
  if (side === -1 && rng.next() < 0.3) {
    const [cx, cy, cz] = at(0);
    a.laundry.push({ x: cx, y: cy + 7.2 + rng.range(0, 3.2), z: cz, rotY: s.dir, s: hw * 2 + 2 });
  }
  // Vespas leaning at the kerb.
  if (rng.next() < 0.14) {
    const [x, y, z] = at(hw + 1.9 + rng.range(0, 1));
    a.scooters.push({ x, y, z, rotY: s.dir + rng.range(-0.5, 0.5), color: rng.pick(SCOOTER_COLORS) });
  }
  scatterPeople({ peopleRng, at, a }, { band: [hw + 1.4, hw + 3.6], chance: 0.5, count: 3 });
};

const bayfront: ZoneBuilder = ({ rng, peopleRng, hw, at, s, a, side, step, progress }) => {
  if (side === BAY_SIDE) {
    // The open bay: a wide azure water sheet beyond the rail.
    //
    // THE FAR EDGE (QA: rooftop-exit — the land/water boundary read as a
    // razor-straight diagonal cut across the block). Two causes, both here:
    //  1. STAIRCASE. The width was a flat `rng.range(60, 80)`, so neighbouring
    //     26 u sheets could differ by 20 u and the bay's outer edge became a
    //     run of hard chords — the same defect the piazza's paving fixed below,
    //     on water. It now rides a smooth along-course wave that neighbours can
    //     only differ on by ~1 u, with the draw still ONE rng call (the whole
    //     point: range() is one next(), and so is next(), so nothing downstream
    //     shifts by a single value).
    //  2. THE ZONE SEAM. The sheets switch on at full width the instant the
    //     lungomare starts (0.30) and off again at 0.62, which planted a
    //     ~70 u wide slab edge straight across the block interior — and the
    //     shortcut's exit lands at exactly 0.303, looking at it. The width now
    //     EASES in and out over BAY_FEATHER, so the bay opens and closes like a
    //     bay instead of arriving as a wall. The floor never drops under ~47:
    //     the fishing boats below sit out to hw + 20, so a narrow feather would
    //     beach them.
    // STILL OPEN (flagged, not mine): the FIRST sheet's up-course end cap is a
    // straight line across the bay at exactly the route's 0.30 zone boundary.
    // Only the zone schedule (config/cities.ts route) or the water system can
    // carry water up-course of the lungomare; this builder is never called
    // before 0.30, so the feather can chamfer that corner but not remove it.
    const t = Math.max(
      0,
      Math.min(1, Math.min(progress - BAY_FROM, BAY_TO - progress) / BAY_FEATHER),
    );
    const open = t * t * (3 - 2 * t); // smoothstep
    // MEASURED: a deep taper (floor 40 over 0.028) simply deleted a third of
    // the bay at the very place the shortcut exit looks at, which trades one
    // artifact for a smaller sea. The feather is short and shallow — enough to
    // chamfer the corner where the first sheet's end cap meets the outer edge,
    // not enough to shrink the water.
    const w = 54 + 16 * open + Math.sin(s.distance * 0.023) * 7 + rng.next() * 4;
    const [x, y, z] = at(hw + w / 2 + 1);
    a.ponds.push({ x, y, z, rotY: s.dir, w, d: step * 2.2, color: MED_SEA, arc: s.distance, side, shore: hw + 1 });
    // Little fishing boats bobbing near the shore.
    if (rng.next() < 0.18) {
      const [bx, by, bz] = at(hw + 6 + rng.range(0, 14));
      a.boats.push({ x: bx, y: by + 0.12, z: bz, rotY: rng.range(0, Math.PI * 2), color: rng.pick(BOAT_COLORS), s: rng.range(0.8, 1.1) });
    }
  } else {
    // Lungomare: a cream sea-wall, palms + umbrella pines, pastel hotels behind.
    const [wx, wy, wz] = at(hw + 1.2);
    a.plain.push({ x: wx, y: wy, z: wz, rotY: s.dir, w: 1.5, h: 1.9, d: step + 1, color: 0xd8cfba });
    const roll = rng.next();
    if (roll < 0.3) {
      const [x, y, z] = at(hw + 3.4 + rng.range(0, 3));
      a.palms.push({ x, y, z, rotY: rng.range(0, Math.PI * 2), s: rng.range(0.85, 1.2) });
    } else if (roll < 0.5) {
      const [x, y, z] = at(hw + 4 + rng.range(0, 4));
      a.pines.push({ x, y, z, rotY: 0, s: rng.range(0.9, 1.3), color: rng.pick(FOLIAGE) });
    }
    if (rng.next() < 0.55) {
      const w = rng.range(10, 15);
      const h = rng.range(9, 17);
      const [x, y, z] = at(hw + 9 + w / 2 + rng.range(0, 5));
      const color = rng.pick(NAPOLI_PASTEL);
      a.buildings.push({ x, y, z, rotY: s.dir, w, h, d: rng.range(10, 14), color });
    }
    // The passeggiata along the lungomare sea wall.
    scatterPeople({ peopleRng, at, a }, { band: [hw + 2.6, hw + 6], chance: 0.5, count: 3 });
  }
};

const castello: ZoneBuilder = ({ rng, peopleRng, hw, at, s, a, progress, marks }) => {
  // Give Castel Nuovo a clear forecourt on its side of the road.
  const nuovoAt = marks.castelNuovo;
  if (nuovoAt != null && Math.abs(progress - nuovoAt) < 0.025) return;
  // Heavy tufa-stone civic blocks, medieval weight, deep cornices.
  if (rng.next() < 0.84) {
    const w = rng.range(10, 16);
    const h = rng.range(10, 20);
    const d = rng.range(10, 16);
    const lat = hw + 3 + w / 2 + rng.range(0, 5);
    const [x, y, z] = at(lat);
    const color = rng.pick(NAPOLI_TUFA);
    a.buildings.push({ x, y, z, rotY: s.dir, w, h, d, color });
    a.plain.push({ x, y: y + h - 0.4, z, rotY: s.dir, w: w + 1.1, h: 1.3, d: d + 1.1, color: darken(color, 0.62) });
  }
  if (rng.next() < 0.16) {
    const [x, y, z] = at(hw + 2.2 + rng.range(0, 2));
    a.pines.push({ x, y, z, rotY: 0, s: rng.range(1.0, 1.4), color: rng.pick(FOLIAGE) });
  }
  scatterPeople({ peopleRng, at, a }, { band: [hw + 1.6, hw + 4.5], chance: 0.4, count: 2 });
};

const piazza: ZoneBuilder = ({ rng, peopleRng, hw, at, s, a, side, step }) => {
  // Wide cream stone paving sweeping away from the road (short slabs + the
  // lifted "sand" layer so sloping ground never pokes through as stripes).
  {
    // The width used to be drawn flat over 40..60, so the plaza's OUTER edge
    // jumped by up to 20 u between neighbouring stretches — a staircase of cut
    // tiles from any elevated angle (the same hard-cut class the water and
    // beach passes killed, on land). It now rides a smooth along-course wave
    // that neighbours can only differ on by ~2 u, with the draw kept to ONE
    // rng call (range() is one next()) so nothing downstream shifts, and a
    // deeper stretch overlap to feather the joints.
    const w = 48 + Math.sin(s.distance * 0.031) * 7 + rng.next() * 4;
    const [x, y, z] = at(hw + w / 2 + 0.5);
    a.sand.push({ x, y, z, rotY: s.dir, w, d: step * 1.45, color: side === -1 ? 0xd8cdb4 : 0xd2c7ae });
  }
  // Paired umbrella pines pacing the plaza edge.
  if (rng.next() < 0.4) {
    const [x, y, z] = at(hw + 5 + rng.range(0, 4));
    a.pines.push({ x, y, z, rotY: 0, s: rng.range(1.1, 1.5), color: rng.pick(FOLIAGE) });
  }
  // Low dignified blocks far back so the colonnade + Galleria stay the heroes.
  if (rng.next() < 0.3) {
    const w = rng.range(12, 18);
    const h = rng.range(8, 13);
    const [x, y, z] = at(hw + 34 + rng.range(0, 16));
    a.buildings.push({ x, y, z, rotY: s.dir, w, h, d: rng.range(10, 16), color: rng.pick(NAPOLI_TUFA) });
  }
  // Crowds drifting across the piazza, some resting on the steps.
  scatterPeople({ peopleRng, at, a }, { band: [hw + 3, hw + 22], chance: 0.6, count: 3, sit: 0.25 });
};

export const NAPOLI_ZONES = { spaccanapoli, bayfront, castello, piazza };
